import {
  ChallengeTemplate,
  ResultFetchDetailsStateType,
  RunAndSubmitCodePostData
} from 'features/AlgoEditor/AlgoEditor.types'
import dynamic from 'next/dynamic'
import { Router } from 'next/router'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useQueryClient } from 'react-query'
import {
  setConsoleOpen,
  setIsCustomTestCases,
  setIsNextChallenge,
  setIsPolling,
  setLoginModalOpen,
  setNextAlgoQuestionSlug,
  setSelectedConsoleTab,
  useAlgoEditorState,
  useAppDispatch,
  useLoginState
} from 'state'
import { useInterval } from 'usehooks-ts'
import { utf8_to_b64 } from 'utils'

import { useMonaco } from '@monaco-editor/react'

import {
  useAlgoSubmissionResult,
  useCodeSubmissionMutation,
  useFetchNextAlgoQuestion,
  useTestCasesSubmissionMutation
} from '../AlgoEditor.services'
import useCode from '../utils/useCode'
import useMonacoThemes from '../utils/useMonacoThemes'

import AlgoEditorBottomBar from './BottomBar'
import LoginEditorModal from './LoginEditorModal'
import ConsoleSection from './ResultSection'

const Editor = dynamic(() => import('@monaco-editor/react'), { ssr: false })

/**
 *
 * @param question
 * @param coding_room_id
 * @param showNextChallenge
 * @param onSubmitSuccessFully
 * @param codeStoreIn
 * @constructor
 */
const EditorSection = ({
  questionId,
  coding_room_id,
  showNextChallenge = true,
  onSubmitSuccessFully,
  codeStoreIn = 'localStorage',
  storagekeyPrefix = 'code',
  challengeTemplates
}: {
  questionId: number
  coding_room_id?: number
  showNextChallenge?: boolean
  onSubmitSuccessFully?: () => void
  codeStoreIn?: 'sessionStorage' | 'localStorage'
  storagekeyPrefix?: string
  challengeTemplates: ChallengeTemplate[]
}) => {
  const loginState = useLoginState()
  const monaco = useMonaco()
  const { settings, customTestCase, isPolling, isNextChallenge } =
    useAlgoEditorState()

  const dispatch = useAppDispatch()
  const queryClient = useQueryClient()

  const seletedTemplate = useMemo(() => {
    return challengeTemplates.filter(
      (template) =>
        template && template.judge_zero_id === settings.language.judge_zero_id
    )[0]
  }, [challengeTemplates, settings.language.judge_zero_id])

  const [code, setCode] = useCode({
    key: `${storagekeyPrefix}-${questionId}-${settings.language.editorLang}`,
    codeStoreIn,
    defaultCode: seletedTemplate?.body || ''
  })

  // const { data: languages } = useLanguages()
  const [intervalTime, setIntervalTime] = useState<number>(700)
  const [refetchCount, setRefetchCount] = useState<number>(0)
  const [resultFetchDetails, setResultFetchDetails] = useState<
    Record<number, ResultFetchDetailsStateType>
  >({
    [questionId]: {
      submissionId: undefined,
      mode: 'test-run'
    }
  })

  const { mutateAsync: submitTestCase, isLoading: submitTestCaseLoading } =
    useTestCasesSubmissionMutation()

  const { mutateAsync: submitCode, isLoading: submitCodeLoading } =
    useCodeSubmissionMutation()

  const { data: finalSubmissionResponse, refetch } = useAlgoSubmissionResult({
    ...resultFetchDetails[questionId]
  })

  const { data: nextAlgoQuestion, isFetched: isFetchedNextAlgoQuestion } =
    useFetchNextAlgoQuestion(questionId, isNextChallenge)

  const { selectedConsoleTab } = useAlgoEditorState()

  if (isFetchedNextAlgoQuestion) {
    nextAlgoQuestion && dispatch(setNextAlgoQuestionSlug(nextAlgoQuestion.slug))
  }

  useEffect(() => {
    Router.events.on('routeChangeComplete', () => {
      setResultFetchDetails({
        [questionId]: { mode: 'test-run', submissionId: undefined }
      })
      dispatch(setIsNextChallenge(false))
    })
  }, [dispatch, questionId])

  const buildTemplate = useCallback(
    (head: string | undefined, code: string, tail: string | undefined) => {
      return tail
        ? utf8_to_b64(head + '\n' + code + '\n' + tail)
        : utf8_to_b64(code) || ''
    },
    []
  )

  /**
   * Handle running of code for example/custom test cases
   * @param isCustomTestCases if its true run the user specified test case
   */
  const handleRunCode = useCallback(
    async (isCustomTestCases: boolean) => {
      if (loginState.isLoggedIn) {
        queryClient.removeQueries([
          'Algo-Submission-Result',
          { submissionId: finalSubmissionResponse?.result_id }
        ])
        dispatch(setIsNextChallenge(false))
        const user_id = loginState.user?.id || 0

        dispatch(setConsoleOpen(true))
        dispatch(setIsCustomTestCases(isCustomTestCases))

        if (isCustomTestCases && !customTestCase) {
          dispatch(setSelectedConsoleTab('custom_tests'))
          return
        }

        dispatch(setSelectedConsoleTab('submission_status'))
        let data: RunAndSubmitCodePostData = {
          user_id,
          challenge_id: questionId,
          source_code: buildTemplate(
            seletedTemplate?.head,
            code,
            seletedTemplate?.tail
          ),
          language: settings.language.id
        }

        if (coding_room_id) {
          data = { ...data, coding_room_id }
        }

        if (isCustomTestCases) {
          data = { ...data, test_case: utf8_to_b64(customTestCase) }
        }

        try {
          const responseId = await submitTestCase(data)
          if (responseId) {
            setResultFetchDetails((prev) => ({
              ...prev,
              [questionId]: {
                mode: 'test-run',
                submissionId: responseId
              }
            }))
            dispatch(setIsPolling(true))
            queryClient.invalidateQueries('Submission-History')
          }
        } catch (e: any) {
          toast.error('something went wrong')
        }
      } else {
        dispatch(setLoginModalOpen(true))
      }
    },
    [
      buildTemplate,
      seletedTemplate?.head,
      seletedTemplate?.tail,
      code,
      coding_room_id,
      customTestCase,
      dispatch,
      finalSubmissionResponse?.result_id,
      loginState.isLoggedIn,
      loginState.user?.id,
      queryClient,
      questionId,
      settings.language.id,
      submitTestCase
    ]
  )

  const handleSubmitCode = useCallback(async () => {
    if (loginState.isLoggedIn) {
      dispatch(setIsNextChallenge(false))
      const user_id = loginState.user?.id || 0
      dispatch(setConsoleOpen(true))
      dispatch(setIsCustomTestCases(false))
      dispatch(setSelectedConsoleTab('submission_status'))
      let data: RunAndSubmitCodePostData = {
        user_id,
        challenge_id: questionId,
        source_code: buildTemplate(
          seletedTemplate?.head,
          code,
          seletedTemplate?.tail
        ),
        language: settings.language.id
      }

      if (coding_room_id) {
        data = { ...data, coding_room_id }
      }

      try {
        const responseId = await submitCode(data)
        if (responseId) {
          setResultFetchDetails((prev) => ({
            ...prev,
            [questionId]: {
              mode: 'submit-run',
              submissionId: responseId
            }
          }))
          dispatch(setIsPolling(true))
          queryClient.invalidateQueries('Submission-History')
        }
      } catch (e: any) {
        if (e.response?.data?.errors[0]?.detail) {
          toast.error(e.response?.data?.errors[0]?.detail)
        } else {
          toast.error('something went wrong')
        }
      }
    } else {
      dispatch(setLoginModalOpen(true))
    }
  }, [
    buildTemplate,
    seletedTemplate?.head,
    seletedTemplate?.tail,
    code,
    coding_room_id,
    dispatch,
    loginState.isLoggedIn,
    loginState.user?.id,
    queryClient,
    questionId,
    settings.language.id,
    submitCode
  ])

  useEffect(() => {
    if (
      finalSubmissionResponse?.status === 'Accepted' &&
      finalSubmissionResponse.total_test_cases ===
        finalSubmissionResponse.passed_test_cases &&
      finalSubmissionResponse.is_submitted
    ) {
      if (showNextChallenge) {
        dispatch(setIsNextChallenge(true))
        dispatch(setSelectedConsoleTab('next_question'))
      } else {
        dispatch(setSelectedConsoleTab('submission_status'))
      }
      onSubmitSuccessFully && onSubmitSuccessFully()
    } else {
      dispatch(setSelectedConsoleTab('submission_status'))
    }
  }, [
    dispatch,
    finalSubmissionResponse,
    onSubmitSuccessFully,
    showNextChallenge
  ])

  useEffect(() => {
    if (refetchCount < 20) {
      if (
        finalSubmissionResponse &&
        Object.keys(finalSubmissionResponse.test_cases).length ===
          finalSubmissionResponse.total_test_cases &&
        finalSubmissionResponse.status !== 'Pending'
      ) {
        dispatch(setIsPolling(false))
        setRefetchCount(0)
        setIntervalTime(700)
        queryClient.invalidateQueries('Submission-History')
      }
    } else {
      dispatch(setIsPolling(false))
      setRefetchCount(0)
      setIntervalTime(700)
      toast.error(
        'Come back after some time to submisisons tab. \nSome issue at our server',
        { duration: 8000 }
      )
      queryClient.invalidateQueries('Submission-History')
    }
  }, [
    dispatch,
    finalSubmissionResponse,
    isNextChallenge,
    nextAlgoQuestion,
    queryClient,
    refetchCount
  ])

  useInterval(
    () => {
      refetch({ fetching: isPolling })
      setIntervalTime((currentInterval) => currentInterval * 1.2)
      setRefetchCount((currentCount) => currentCount + 1)
    },
    // Delay in milliseconds or null to stop it
    isPolling ? intervalTime : null
  )

  useMonacoThemes(monaco)

  return (
    <div className="relative w-full h-full flex flex-col">
      <div className="w-full flex-1 overflow-hidden">
        <Editor
          width="100%"
          height="100%"
          theme={settings.theme.id}
          language={settings.language.editorLang}
          loading={'Loading...'}
          value={code}
          onChange={(value) => setCode(value || '')}
          options={{
            wordWrap: settings.wordWrap ? 'on' : 'off',
            fontSize: settings.fontSize,
            minimap: { enabled: settings.minimap }
          }}
        />
      </div>

      <div className="flex flex-col w-full absolute bottom-0 bg-zinc-900 z-20">
        <ConsoleSection
          handleRunCode={handleRunCode}
          submitTestCaseLoading={submitTestCaseLoading}
          submitCodeLoading={submitCodeLoading}
          finalSubmissionResponse={finalSubmissionResponse}
        />
        {selectedConsoleTab !== 'next_question' && (
          <AlgoEditorBottomBar
            handleRunCode={handleRunCode}
            handleSubmitCode={handleSubmitCode}
          />
        )}

        <LoginEditorModal />
      </div>
    </div>
  )
}

export default EditorSection
